import React from 'react';
import type {RefObject} from 'react';

import styled from '@emotion/styled';
import * as motion from 'motion/react-m';

import {usePopoverAnimation} from './usePopoverAnimation';
import {borderRadius, clipPath, inset} from './utils';

import type {PopoverState} from './usePopoverAnimation';
import type {WithChildren} from '../../../utils/childTypes';
import type {Variant} from 'motion';

export type PopoverAnimation = {
    enter: Variant;
    exit: Variant;
    initial: Variant;
};

export interface PopoverProps {
    /**
     * An optional property that determines the alignment of the component's content. It
     * can take the values `'center'` or `'left'`, allowing for flexible layout options. When not specified,
     * the component may default to a standard alignment or inherit from its parent container.
     */
    alignment?: 'left' | 'right';
    /**
     * An optional property that specifies the name of the anchor element for the popover.
     * This property is used to link the popover to a specific anchor in the DOM,
     * allowing for precise positioning of the popover relative to the anchor element.
     */
    anchorName: string;
    /**
     * An optional property that specifies the maximum width of the popover content. This property is used to constrain the width of the popover, ensuring that it does not exceed a certain size and maintains a visually appealing layout. The value can be defined in any valid CSS unit (e.g., `px`, `em`, `%`), allowing for flexible design options.
     */
    maxWidth?: string;
    /**
     * An optional property that specifies the minimum width of the popover content. This property is used to ensure that the popover maintains a certain width, even when the content is minimal, providing a consistent and visually balanced appearance. The value can be defined in any valid CSS unit (e.g., `px`, `em`, `%`), allowing for flexible design options.
     */
    minWidth?: string;
    /**
     * A required property that assigns a unique identifier to the component. This identifier is
     * used primarily for testing purposes, enabling developers to easily locate and interact with the
     * component in automated test scripts or debugging tools.
     */
    testId?: string;
    /**
     * An optional property that specifies the height of the triangle element in the popover.
     * This property is used to adjust the size of the triangle relative to the popover content,
     * allowing for precise control over the visual appearance of the popover and its associated triangle.
     */
    triangleHeight?: number;
    /**
     * An optional property that specifies the horizontal offset of the triangle element.
     * This property is used to adjust the position of the triangle relative to the popover content,
     * allowing for precise control over the visual alignment of the popover and its associated triangle.
     *
     * @default '+ 0px'
     */
    triangleOffset?: `- ${number}px` | `+ ${number}px`;
    /**
     * An optional property that specifies the position of the triangle along the horizontal axis.
     * This property is used to adjust the position of the triangle relative to the popover content,
     * allowing for precise control over the visual alignment of the popover and its associated triangle.
     */
    trianglePosition?: number;
}

/**
 * Factory function for creating a popover component.
 *
 * This function creates a popover component that can be used to display content
 * in a popover dialog. The popover component supports animation and can be
 * controlled using the `openPopover` and `closePopover` functions returned by
 * this factory.
 *
 * @param state     Ref object for managing the popover state.
 * @param animation The animation to use for the popover.
 * @returns The popover component.
 *
 * @example
 * ```tsx
 * const state = useRef<Dispatch<SetStateAction<boolean>>>(null);
 * const Popover = popoverFactory(state);
 * ```
 */
const popoverFactory = (state: RefObject<PopoverState>, animation: PopoverAnimation) => {
    /**
     * Popover component that renders a positioned dialog relative to an anchor element.
     * This component utilizes CSS anchor positioning and motion animations to display content
     * in a popover with customizable alignment, triangle positioning, and animation states.
     *
     * @param props                  The component props.
     * @param props.alignment        An optional property that determines the alignment of the popover relative to its anchor.
     * @param props.anchorName       The name of the anchor element for the popover positioning.
     * @param props.children         The content to be rendered inside the popover.
     * @param props.maxWidth         An optional property that specifies the maximum width of the popover content.
     * @param props.minWidth         An optional property that specifies the minimum width of the popover content.
     * @param props.testId           A unique identifier for testing purposes.
     * @param props.triangleHeight   The height of the triangle pointer in pixels.
     * @param props.triangleOffset   The horizontal offset of the triangle from its default position.
     * @param props.trianglePosition The position of the triangle along the horizontal axis as a percentage.
     * @returns A React functional component that renders an animated popover dialog.
     *
     * @example
     * ```tsx
     * <Popover
     *   anchorName="--my-anchor"
     *   alignment="center"
     *   triangleHeight={10}
     *   triangleOffset="+ 5px"
     *   testId="my-popover"
     * >
     *   <p>Popover content</p>
     * </Popover>
     * ```
     */
    const Popover = ({
        alignment = 'left',
        anchorName,
        children,
        maxWidth = 'max-content',
        minWidth = 'max-content',
        testId = 'Popover',
        triangleHeight = 8,
        triangleOffset = '+ 0px',
        trianglePosition = 50
    }: WithChildren<PopoverProps>) => {
        const {
            controls,
            popover,
            showContent
        } = usePopoverAnimation(state);

        return (
            <Wrapper
                ref={popover}
                $alignment={alignment}
                $anchorName={anchorName}
                $borderRadius={5}
                $maxWidth={maxWidth}
                $minWidth={minWidth}
                $triangleBaseWidth={15}
                $triangleHeight={triangleHeight}
                $triangleOffset={triangleOffset}
                $trianglePosition={trianglePosition}
                animate={controls}
                custom={alignment}
                data-cy={testId}
                variants={animation}
                $isTop
            >
                {showContent && children}
            </Wrapper>
        );
    };

    Popover.displayName = 'Popover';

    return Popover;
};

export {popoverFactory};

interface WrapperProps {
    $alignment?: 'left' | 'right';
    $anchorName: string;
    $borderRadius: number;
    $isTop: boolean;
    $maxWidth: string;
    $minWidth: string;
    $triangleBaseWidth: number;
    $triangleHeight: number;
    $triangleOffset: `- ${number}px` | `+ ${number}px`;
    $trianglePosition: number;
}

const Wrapper = styled(motion.dialog)<WrapperProps>`
    border: none;
    border-radius: ${borderRadius};
    box-shadow: ${({theme}) => theme.boxShadows.popOverShadow};
    flex-direction: column;
    height: max-content;
    left: ${({$alignment}) => ($alignment === 'left' ? 'anchor(center)' : null)};
    margin: 0;
    margin-top: ${({$triangleHeight}) => $triangleHeight}px;
    max-width: ${({$maxWidth}) => $maxWidth};
    min-width: ${({$minWidth}) => $minWidth};
    opacity: 0;
    outline: none;
    overflow: visible;
    padding: 0;
    position: absolute;
    position-anchor: ${({$anchorName}) => $anchorName};
    right: ${({$alignment}) => ($alignment === 'right' ? 'anchor(center)' : null)};
    top: ${({$anchorName}) => `anchor(${$anchorName} bottom)`};
    transform: translateX(0px) translateY(80px);
    width: 100%;
    z-index: 99;

    &::before {
        background: inherit;
        box-shadow: ${({theme}) => theme.boxShadows.popOverShadow};
        clip-path: ${clipPath};
        content: "";
        inset: ${inset};
        position: absolute;
        z-index: -1;
    }
`;